import { useCallback, useEffect, useState } from "react";
import { APP_VERSION } from "./version";

// ═══════════════════════════════════════════════════════════════
// PWA-PÄIVITYKSEN HAVAITSEMINEN
//
// Auki oleva PWA-ikkuna ei hae index.html:ää uudelleen koskaan. Välimuisti-
// otsakkeet ovat kunnossa (firebase.json asettaa index.html:lle no-store),
// mutta niillä ei ole merkitystä jos sivua ei ladata. Käyttäjä jäi siis
// vanhaan bundleen deployn jälkeen, mahdollisesti päiviksi.
//
// Se ei ole kosmeettinen ongelma: kirjautuminen epäonnistui vanhalla
// bundlella ja virheilmoitus väitti verkkovirhettä. Vika oli korjattu
// palvelimella eikä korjaus tavoittanut käyttäjää.
//
// Service worker ei auta tässä — public/firebase-messaging-sw.js ei
// välimuistita mitään, siinä ei ole fetch-käsittelijää lainkaan.
//
// Ratkaisu on kysyä palvelimelta suoraan. public/version.json pidetään
// synkassa src/version.ts:n kanssa, joten niiden ero tarkoittaa että
// selaimessa on vanha koodi.
// ═══════════════════════════════════════════════════════════════

/** Kuinka usein tarkistetaan taustalla. */
const POLL_MS = 30 * 60 * 1000;

/**
 * Onko palvelimella uudempi versio kuin tässä bundlessa.
 *
 * Natiiviapissa tätä ei kysytä: appi paketoi oman kopionsa
 * web-sisällöstä (capacitor.config.ts, `webDir: "build"`), joten
 * palvelimen version.json kertoisi verkkoversiosta eikä siitä mitä
 * laitteella ajetaan — ja kehotus päivittää olisi suorastaan väärä,
 * koska päivitys tulee kaupasta.
 */
export function useAppUpdate(): {
  updateReady: boolean;
  /**
   * Palvelimen versio, kun se on saatu haettua. Tämä näytetään
   * versiomerkissä (components/VersionTag.tsx) yhdessä selaimen oman
   * version kanssa, jotta "ei päivity" on luettavissa suoraan ruudulta
   * eikä sitä tarvitse päätellä.
   *
   * null natiiviapissa ja ennen ensimmäistä onnistunutta hakua.
   */
  serverVersion: string | null;
  reload: () => void;
} {
  const [updateReady, setUpdateReady] = useState(false);
  const [serverVersion, setServerVersion] = useState<string | null>(null);

  const check = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = (await res.json()) as { version?: string };
      if (!data.version) return;
      setServerVersion(data.version);
      if (data.version !== APP_VERSION) setUpdateReady(true);
    } catch {
      // Verkkokatkos ei ole päivitys. Yritetään seuraavalla kierroksella.
    }
  }, []);

  useEffect(() => {
    let native = false;
    try {
      // Capacitor injektoi tämän vain natiivikuoressa.
      native = !!(window as { Capacitor?: { isNativePlatform?: () => boolean } })
        .Capacitor?.isNativePlatform?.();
    } catch { /* selain */ }
    if (native) return;

    check();
    const timer = window.setInterval(check, POLL_MS);
    // Palaaminen välilehteen on luonteva hetki: käyttäjä on juuri
    // palaamassa työhön eikä keskeydy kesken kaiken.
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateReady, serverVersion, reload };
}
